import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Activity, ArrowRight, ScanLine } from "lucide-react";
import useSpeechSynthesis from "@/hooks/useSpeechSynthesis";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function XRay() {
  const [queue, setQueue] = useState(null);
  const [maleTokens, setMaleTokens] = useState([]);
  const [femaleTokens, setFemaleTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { speakWithGenderVoices } = useSpeechSynthesis();

  const fetchQueue = async () => {
    try {
      const token = localStorage.getItem("token");
      const [queueRes, maleRes, femaleRes] = await Promise.all([
        axios.get(`${API}/departments/xray/queue`, { headers: { Authorization: `Bearer ${token}` } }),
        // Get tokens that completed physical exam (ready for X-Ray)
        axios.get(`${API}/tokens?status=physical_done&gender=Male`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tokens?status=physical_done&gender=Female`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setQueue(queueRes.data);
      setMaleTokens(maleRes.data);
      setFemaleTokens(femaleRes.data);
    } catch (error) {
      console.error("Failed to fetch queue", error);
    }
  };

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleCallNext = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/departments/call-next`,
        { department: "xray" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const tokenNumber = response.data.token_number;
        toast.success(`Token ${tokenNumber} called!`, {
          description: "Please proceed to X-Ray Department",
        });
        
        // Text-to-speech announcement in English and Telugu
        const message = `Token ${tokenNumber}, please proceed to X-Ray Department`;
        speakWithGenderVoices(message);
        
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi6Dzvjaj0APHGy98+CbTRAMUqvr8q5jHgU7n+n0xnksBSmAx/LZkEAKE1m48N2kUxQLSKXl8r5xIQYuf9T52ZBBChZmvvTinlEQC1Kp5O+wZB4EO5rj88h1KwUqfsXu1I4+CRJatPPepVYUC0me4fC+ciEGL4DN99mPQQoVZ77z4Z9RDwtRp+Lur2IdBTug6fTGeCsFKX/H8tiNQAoVYr/04p5SEAtPpe/us2YDBY6a4fLC');
        audio.play().catch(() => {});
        fetchQueue();
      } else {
        toast.info("No tokens available for current batch");
      }
    } catch (error) {
      toast.error("Failed to call next token");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!queue?.current_token) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/tokens/${queue.current_token}/status`,
        { status: "completed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`X-Ray completed for ${queue.current_token}!`, {
        description: "Patient can collect reports",
      });
      fetchQueue();
    } catch (error) {
      toast.error("Failed to complete X-Ray");
    }
  };

  const handleCompleteAndCallNext = async () => {
    if (!queue?.current_token) {
      await handleCallNext();
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/tokens/${queue.current_token}/status`,
        { status: "completed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`X-Ray completed for ${queue.current_token}!`);
      
      setTimeout(async () => {
        await handleCallNext();
      }, 500);
    } catch (error) {
      toast.error("Failed to complete X-Ray");
    }
  };

  const handleSelectToken = async (tokenNumber) => {
    try {
      const token = localStorage.getItem("token");
      
      // Update token status to at_xray
      await axios.put(
        `${API}/tokens/${tokenNumber}/status`,
        { status: "at_xray" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Token ${tokenNumber} selected!`);
      const message = `Token ${tokenNumber}, please proceed to X-Ray Department`;
      speakWithGenderVoices(message);
      
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi6Dzvjaj0APHGy98+CbTRAMUqvr8q5jHgU7n+n0xnksBSmAx/LZkEAKE1m48N2kUxQLSKXl8r5xIQYuf9T52ZBBChZmvvTinlEQC1Kp5O+wZB4EO5rj88h1KwUqfsXu1I4+CRJatPPepVYUC0me4fC+ciEGL4DN99mPQQoVZ77z4Z9RDwtRp+Lur2IdBTug6fTGeCsFKX/H8tiNQAoVYr/04p5SEAtPpe/us2YDBY6a4fLC');
      audio.play().catch(() => {});
      
      fetchQueue();
    } catch (error) {
      toast.error("Failed to select token");
    }
  };

  const handleRecall = async (tokenNumber) => {
    try {
      const token = localStorage.getItem("token");
      
      // Remove from skipped tokens and add back to queue
      await axios.delete(
        `${API}/departments/xray/skipped/${tokenNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Token ${tokenNumber} recalled to queue!`);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to recall token");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const currentBatch = queue?.xray_current_batch || "male";
  const maleCount = queue?.xray_male_count || 0;
  const femaleCount = queue?.xray_female_count || 0;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="xray-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-purple-600 flex items-center justify-center">
                <ScanLine className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900">X-Ray Department</h1>
                <p className="text-purple-600 font-semibold mt-1">Groups of 10 (Alternating Male/Female)</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => window.open('/display', '_blank')}
              data-testid="open-display-button"
              className="h-10"
            >
              <Activity className="mr-2 h-4 w-4" />
              Open Display
            </Button>
            <Button variant="outline" onClick={handleLogout} data-testid="logout-button" className="h-10">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="hover-lift">
            <CardHeader className="pb-3">
              <CardDescription>Current Batch</CardDescription>
              <CardTitle className="text-2xl capitalize">
                <Badge className={currentBatch === "male" ? "bg-indigo-600 text-white text-lg px-4 py-1" : "bg-rose-600 text-white text-lg px-4 py-1"}>
                  {currentBatch}
                </Badge>
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="hover-lift">
            <CardHeader className="pb-3">
              <CardDescription>Male Batch Progress</CardDescription>
              <CardTitle className="text-2xl text-indigo-600">{maleCount} / 10</CardTitle>
            </CardHeader>
          </Card>
          <Card className="hover-lift">
            <CardHeader className="pb-3">
              <CardDescription>Female Batch Progress</CardDescription>
              <CardTitle className="text-2xl text-rose-600">{femaleCount} / 10</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="lg:col-span-2 border-purple-200">
            <CardHeader className="bg-purple-50">
              <CardTitle>Current Token</CardTitle>
              <CardDescription>Patient at X-Ray</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {queue?.current_token ? (
                <div className="space-y-4">
                  <div className="text-center py-8 bg-purple-50 rounded-lg border-2 border-purple-300" data-testid="current-token-display">
                    <div className="font-mono text-7xl font-black text-purple-900 tracking-widest">
                      {queue.current_token}
                    </div>
                  </div>
                  <Button
                    onClick={handleCompleteAndCallNext}
                    data-testid="complete-and-next-button"
                    className="w-full h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700"
                  >
                    Complete X-Ray & Call Next
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center py-12 text-slate-400">
                    <p className="text-lg">No patient at X-Ray</p>
                    <p className="text-sm mt-2">Select a patient from below or click to call next</p>
                  </div>
                  <Button
                    onClick={handleCallNext}
                    disabled={loading}
                    data-testid="call-next-button"
                    className="w-full h-14 text-lg font-semibold bg-purple-600 hover:bg-purple-700"
                  >
                    Call Next Patient
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle className="text-indigo-900">Male Queue</CardTitle>
                <CardDescription>{maleTokens.length} male patients - Click to select</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3" data-testid="male-tokens-list">
                {maleTokens.slice(0, 10).map((token) => (
                  <button
                    key={token.token_number}
                    onClick={() => handleSelectToken(token.token_number)}
                    className="p-3 bg-indigo-50 rounded-lg border border-indigo-200 text-center hover:bg-indigo-100 hover:border-indigo-400 cursor-pointer transition-colors"
                    data-testid={`male-token-${token.token_number}`}
                  >
                    <div className="font-mono text-lg font-bold text-indigo-900">{token.token_number}</div>
                    <Badge className="mt-1 bg-indigo-600 text-white text-xs">Male</Badge>
                  </button>
                ))}
              </div>
              {maleTokens.length === 0 && (
                <div className="text-center py-6 text-slate-400">
                  <p className="text-sm">No male patients</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle className="text-rose-900">Female Queue</CardTitle>
                <CardDescription>{femaleTokens.length} female patients - Click to select</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3" data-testid="female-tokens-list">
                {femaleTokens.slice(0, 10).map((token) => (
                  <button
                    key={token.token_number}
                    onClick={() => handleSelectToken(token.token_number)}
                    className="p-3 bg-rose-50 rounded-lg border border-rose-200 text-center hover:bg-rose-100 hover:border-rose-400 cursor-pointer transition-colors"
                    data-testid={`female-token-${token.token_number}`}
                  >
                    <div className="font-mono text-lg font-bold text-rose-900">{token.token_number}</div>
                    <Badge className="mt-1 bg-rose-600 text-white text-xs">Female</Badge>
                  </button>
                ))}
              </div>
              {femaleTokens.length === 0 && (
                <div className="text-center py-6 text-slate-400">
                  <p className="text-sm">No female patients</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}